import { Outlet, NavLink, useLocation } from "react-router-dom";
import { LayoutDashboard, Users, CalendarCheck2, ScrollText, BadgeCheck, Download } from "lucide-react";
import { endpoints } from "@/lib/api";

const LOGO_URL =
  "https://customer-assets-jai6qajn.emergentagent.net/job_f352c4ae-9f78-42ce-82e7-f808f08253be/artifacts/ho2kn92p_hold-it-logo-primary-dark.png";

const nav = [
  { to: "/", label: "Overview", icon: LayoutDashboard, testId: "nav-overview", end: true },
  { to: "/participants", label: "Participants", icon: Users, testId: "nav-participants" },
  { to: "/commitments", label: "Commitment Tracker", icon: CalendarCheck2, testId: "nav-commitments" },
  { to: "/evidence", label: "Evidence Results", icon: BadgeCheck, testId: "nav-evidence" },
];

export default function Layout() {
  const location = useLocation();
  return (
    <div className="min-h-screen flex" style={{ background: "var(--obsidian)" }}>
      {/* Sidebar */}
      <aside
        className="w-[260px] shrink-0 flex flex-col border-r"
        style={{ background: "var(--surface)", borderColor: "var(--hairline)" }}
        data-testid="sidebar"
      >
        <div className="px-6 pt-7 pb-5">
          <img
            src={LOGO_URL}
            alt="Hold-It"
            className="h-8 w-auto"
            data-testid="brand-logo"
            style={{ imageRendering: "auto" }}
          />
          <div
            className="mt-3 text-[10px] tracking-[0.18em] uppercase font-mono-num"
            style={{ color: "var(--cream-muted)" }}
          >
            Evidence Command Center
          </div>
        </div>

        <div className="gold-rule mx-6 my-2" />

        <nav className="flex-1 px-3 py-4 space-y-1" data-testid="sidebar-nav">
          {nav.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              data-testid={item.testId}
              className={({ isActive }) =>
                `flex items-center gap-3 px-4 py-2.5 rounded-md text-[13px] transition-all ${
                  isActive
                    ? "text-[var(--obsidian)] font-medium"
                    : "text-[var(--cream-dim)] hover:text-[var(--cream)] hover:bg-[var(--surface-2)]"
                }`
              }
              style={({ isActive }) =>
                isActive ? { background: "var(--gold)" } : {}
              }
            >
              <item.icon className="w-4 h-4" strokeWidth={1.6} />
              <span>{item.label}</span>
            </NavLink>
          ))}
        </nav>

        <div className="px-6 pb-6 pt-3 space-y-3">
          <div className="gold-rule" />
          <div className="text-[10px] tracking-[0.15em] uppercase font-mono-num" style={{ color: "var(--cream-muted)" }}>
            Program Boundary
          </div>
          <div className="text-[11px] leading-relaxed" style={{ color: "var(--cream-dim)" }}>
            Your money remains at your existing financial institution.{" "}
            <span style={{ color: "var(--gold-light)" }}>Hold-It does not hold, transfer, freeze, or restrict</span>{" "}
            your funds during the Evidence Program.
          </div>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 min-w-0 flex flex-col">
        <div
          className="sticky top-0 z-20 border-b"
          style={{ background: "rgba(10,10,12,0.8)", backdropFilter: "blur(12px)", borderColor: "var(--hairline)" }}
        >
          <div className="container-hi h-14 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <span
                className="chip gold"
                data-testid="demo-badge"
              >
                <ScrollText className="w-3 h-3" />
                Demo — Fictional Sample Data
              </span>
              <span className="text-[11px]" style={{ color: "var(--cream-muted)" }}>
                Stage 1 Evidence Program
              </span>
            </div>
            <div className="flex items-center gap-2">
              <a
                href={endpoints.exportCsvUrl()}
                target="_blank"
                rel="noreferrer"
                className="btn-ghost inline-flex items-center gap-2"
                data-testid="export-csv-btn"
              >
                <Download className="w-3.5 h-3.5" />
                Export CSV
              </a>
            </div>
          </div>
        </div>
        <div key={location.pathname} className="flex-1 fade-up">
          <Outlet />
        </div>
        <footer className="container-hi py-8 text-[11px]" style={{ color: "var(--cream-muted)" }}>
          Hold-It Evidence Command Center · Prototype build · No live participants, no live funds.
        </footer>
      </main>
    </div>
  );
}
